import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { toast } from "sonner";
import { Link2, Unlink, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";

const STATUS = {
  connected: { label: "Connecté", cls: "bg-emerald-900/40 text-emerald-400", Icon: CheckCircle2 },
  expired: { label: "Connexion expirée", cls: "bg-[#141414] text-[#E6412D]", Icon: AlertTriangle },
  disconnected: { label: "Déconnecté", cls: "bg-zinc-800 text-zinc-400", Icon: Unlink },
};

export const QuickBooksSettings = ({ settings, setSettings }) => {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mapKeys, setMapKeys] = useState([]);
  const [missing, setMissing] = useState([]);
  const qbo = settings.qbo || {};
  const setQbo = (patch) => setSettings(s => ({ ...s, qbo: { ...(s.qbo || {}), ...patch } }));

  const loadRefs = useCallback(async () => {
    const [it, tc, ac] = await Promise.all([
      api.get("/qbo/items").catch(() => ({ data: [] })),
      api.get("/qbo/tax-codes").catch(() => ({ data: [] })),
      api.get("/qbo/accounts").catch(() => ({ data: [] })),
    ]);
    setItems(it.data); setTaxCodes(tc.data); setAccounts(ac.data);
  }, []);

  const loadStatus = useCallback(async () => {
    const r = await api.get("/qbo/status").catch(() => null);
    if (!r) return;
    setStatus(r.data);
    if (r.data.status === "connected") loadRefs();
  }, [loadRefs]);

  useEffect(() => {
    loadStatus();
    api.get("/qbo/mapping-check").then(r => { setMapKeys(r.data.keys); setMissing(r.data.missing); }).catch(() => {});
    const q = new URLSearchParams(window.location.search).get("qbo");
    if (q === "connected") toast.success("QuickBooks connecté");
    if (q === "error") toast.error("La connexion QuickBooks n'a pas fonctionné. Réessayez.");
  }, [loadStatus]);

  const connect = async () => {
    setBusy(true);
    try { const { data } = await api.get("/qbo/oauth/start"); window.location.assign(data.url); }
    catch (e) { toast.error(e.response?.data?.detail || "Impossible de démarrer la connexion"); setBusy(false); }
  };
  const resetCounter = async () => {
    try { const { data } = await api.post("/qbo/reset-counter");
      setQbo({ next_number: data.next_number });
      toast.success(`Prochaine facture : ${data.next_doc_number}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const disconnect = async () => {    setBusy(true);
    try { await api.post("/qbo/disconnect"); toast.success("QuickBooks déconnecté"); await loadStatus(); }
    catch { toast.error("Erreur"); } finally { setBusy(false); }
  };

  const st = STATUS[status?.status || "disconnected"];
  const Icon = st.Icon;

  return (
    <div data-testid="qbo-settings">
      <h3 className="font-display text-xl mt-8 mb-1">QuickBooks Online</h3>
      <p className="text-xs text-zinc-500 mb-4">
        Chaque séance payée créera une facture et un paiement dans QuickBooks. Les identifiants Intuit restent sur le serveur.
      </p>

      <div className="kr-card p-4 flex flex-wrap items-center gap-3">        <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${st.cls}`} data-testid="qbo-status">
          <Icon className="w-3.5 h-3.5"/>{st.label}
        </span>
        <span className="text-xs px-2 py-1 rounded-full bg-[#141414] text-[#E6412D]" data-testid="qbo-env">
          {status?.environment === "sandbox" ? "Bac à sable (aucune écriture dans votre vrai QuickBooks)" : "Mode réel"}
        </span>
        {status?.company_name && <span className="text-xs text-zinc-400" data-testid="qbo-company">{status.company_name}</span>}
        {status?.status === "connected"
          ? <>
              <button onClick={loadRefs} className="kr-btn kr-btn-ghost text-sm" data-testid="qbo-reload-refs"><RefreshCw className="w-4 h-4"/>Recharger les listes</button>
              <button onClick={disconnect} disabled={busy} className="kr-btn kr-btn-ghost text-sm" data-testid="qbo-disconnect"><Unlink className="w-4 h-4"/>Déconnecter</button>
            </>
          : <button onClick={connect} disabled={busy || !status?.configured} className="kr-btn kr-btn-primary text-sm" data-testid="qbo-connect"><Link2 className="w-4 h-4"/>Connecter QuickBooks</button>}
        {!status?.configured && <span className="text-xs text-[#E6412D]">Identifiants Intuit absents du serveur.</span>}
        {status?.status === "expired" && <span className="text-xs text-[#E6412D]" data-testid="qbo-expired-alert">{status.status_detail || "Reconnectez QuickBooks."}</span>}
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        <div><label className="kr-caption">Préfixe de la série</label>
          <input value={qbo.prefix ?? "SEANCE"} onChange={e => setQbo({ prefix: e.target.value.toUpperCase() })} data-testid="qbo-prefix"/></div>
        <div><label className="kr-caption">Prochain numéro</label>
          <input type="number" min="1" value={qbo.next_number ?? 1} onChange={e => setQbo({ next_number: parseInt(e.target.value) || 1 })} data-testid="qbo-next-number"/></div>
        <div><label className="kr-caption">Année de la série</label>
          <input type="number" value={qbo.year ?? new Date().getFullYear()} onChange={e => setQbo({ year: parseInt(e.target.value) || new Date().getFullYear() })} data-testid="qbo-year"/></div>
        <div className="sm:col-span-3 text-xs text-zinc-500 flex flex-wrap items-center gap-3">
          <span>Prochaine facture : <span className="gold-text">{`${qbo.prefix || "SEANCE"}-${qbo.year || new Date().getFullYear()}-${String(qbo.next_number || 1).padStart(4, "0")}`}</span> · remise à 0001 chaque 1<sup>er</sup> janvier</span>
          <button onClick={resetCounter} className="kr-btn kr-btn-ghost text-xs" data-testid="qbo-reset-counter">Recaler sur le prochain numéro libre</button>
        </div>
        <div><label className="kr-caption">Code de taxe (TPS/TVQ)</label>
          <select value={qbo.tax_code_id ?? ""} onChange={e => setQbo({ tax_code_id: e.target.value })} data-testid="qbo-tax-code">
            <option value="">— choisir —</option>
            {taxCodes.map(tc => <option key={tc.Id} value={tc.Id}>{tc.Name}</option>)}
          </select></div>
        <div><label className="kr-caption">Compte de dépôt</label>
          <select value={qbo.deposit_account_id ?? ""} onChange={e => setQbo({ deposit_account_id: e.target.value })} data-testid="qbo-deposit-account">
            <option value="">— choisir —</option>
            {accounts.map(a => <option key={a.Id} value={a.Id}>{a.Name}</option>)}
          </select></div>
      </div>

      <h4 className="kr-caption mt-6 mb-2">Correspondance services → produits QuickBooks</h4>
      {missing.length > 0 && (
        <div className="kr-card p-3 mb-3 text-xs flex items-start gap-2" style={{ color: "#E6412D" }} data-testid="qbo-missing-mapping">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
          <span>Correspondance manquante : <b>{missing.map(m => m.label).join(", ")}</b>. Tant qu'elle manque, la facture d'une séance payée reste « en attente » au lieu d'être créée.</span>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        {mapKeys.map(({ key, label }) => (
          <div key={key}><label className="kr-caption">{label}</label>
            <select value={qbo.service_items?.[key] ?? ""} data-testid={`qbo-item-${key}`}
              onChange={e => setQbo({ service_items: { ...(qbo.service_items || {}), [key]: e.target.value } })}>
              <option value="">— choisir —</option>
              {items.map(it => <option key={it.Id} value={it.Id}>{it.Name}</option>)}
            </select></div>
        ))}
      </div>
      {status?.status === "connected" && !items.length && (
        <p className="text-xs text-[#E6412D] mt-3" data-testid="qbo-no-items">Aucun produit/service trouvé dans QuickBooks. Créez-les dans votre entreprise bac à sable, puis « Recharger les listes ».</p>
      )}
    </div>
  );
};
